/**
 * Copilot launch context — lets surface-level UI (alarms, batch trace,
 * correlation insights, entity detail) hand off a *specific problem*
 * to the copilot with one click.
 *
 * Phase 0 wires this to canned starter messages. Phase 4 replaces the
 * canned starters with a /v1/twin/copilot/sessions create call that
 * passes the context payload to the LLM as a tool input.
 *
 * URL form: /app/twin/copilot?ctx=alarm:a-1
 *           /app/twin/copilot?ctx=batch:H-04820&topic=porosity
 *           /app/twin/copilot?ctx=correlation:moisture_porosity
 *           /app/twin/copilot?ctx=entity:n_furnace
 */

import type { CopilotMessage, ProposalApply } from "@/lib/widgets/__foundry_fixtures";
import { FX_ALARMS } from "@/lib/widgets/__fixtures";
import { FX_BATCHES } from "@/lib/widgets/__foundry_fixtures";

export type CopilotContextKind =
  | "alarm"
  | "batch"
  | "correlation"
  | "entity"
  | "model_authoring";

export interface CopilotContext {
  kind: CopilotContextKind;
  /** Free-form id — alarm id, heat number, correlation id, entity id. */
  ref: string;
  /** Optional topic filter (e.g. "porosity"). */
  topic?: string;
  /**
   * Optional WHERE the context originates (A2) — for an alarm, the bound
   * Tier-2 entity + stage, e.g. "Induction Furnace · Heat". Carried through
   * the deep-link so the consultant's opening turn names the equipment/stage
   * even when the alarm id isn't a known fixture (live mode). URL param `where`.
   */
  where?: string;
}

export function parseCopilotContext(search: URLSearchParams): CopilotContext | null {
  const raw = search.get("ctx");
  if (!raw) return null;
  const [kind, ref] = raw.split(":", 2);
  if (!kind || !ref) return null;
  if (!["alarm", "batch", "correlation", "entity", "model_authoring"].includes(kind)) return null;
  return {
    kind: kind as CopilotContextKind,
    ref,
    topic: search.get("topic") ?? undefined,
    where: search.get("where") ?? undefined,
  };
}

export function buildCopilotHref(ctx: CopilotContext): string {
  const params = new URLSearchParams();
  params.set("ctx", `${ctx.kind}:${ctx.ref}`);
  if (ctx.topic) params.set("topic", ctx.topic);
  if (ctx.where) params.set("where", ctx.where);
  return `/app/twin/copilot?${params.toString()}`;
}

/**
 * Generate the opening (user + assistant) message pair for a given
 * launch context. Phase 0: deterministic canned content; Phase 4 swaps
 * the assistant turn for a real Claude call.
 */
export function makeContextStarterMessages(ctx: CopilotContext): CopilotMessage[] {
  const now = new Date().toISOString();

  if (ctx.kind === "alarm") {
    const alarm = FX_ALARMS.find((a) => a.id === ctx.ref);
    // Live mode: the alarm id won't match a Phase-0 fixture. Fall back to a
    // generic investigation turn that still names WHERE it fired (A2 — the
    // bound entity + stage threaded through `ctx.where`), so the consultant
    // opens with the location instead of returning nothing.
    if (!alarm) {
      if (!ctx.where) return [];
      const at = ` at ${ctx.where}`;
      return [
        {
          id: `m-user-${Date.now()}`,
          role: "user",
          content: `Investigate the active alarm${at}. What's happening and what should we do?`,
          timestamp: now,
        },
        {
          id: `m-assistant-${Date.now() + 1}`,
          role: "assistant",
          content: `Looking into the alarm on ${ctx.where}. Phase 0 — this turn is canned; Phase 4 grounds it in the live tag stream for this equipment + the foundry pack's defect taxonomy + AFS clauses.`,
          timestamp: now,
        },
      ];
    }
    const at = ctx.where ? ` (${ctx.where})` : "";
    const userText = `Investigate alarm: "${alarm.title}"${at}. What's happening and what should we do?`;
    return [
      {
        id: `m-user-${Date.now()}`,
        role: "user",
        content: userText,
        timestamp: now,
      },
      {
        id: `m-assistant-${Date.now() + 1}`,
        role: "assistant",
        content: alarmAssistantText(alarm),
        timestamp: now,
        proposals: alarmProposals(alarm),
      },
    ];
  }

  if (ctx.kind === "batch") {
    const batch = FX_BATCHES.find((b) => b.heatNumber === ctx.ref);
    if (!batch) return [];
    const userText =
      batch.status === "fail"
        ? `Why did heat ${batch.heatNumber} fail QC?`
        : `Walk me through heat ${batch.heatNumber}.`;
    return [
      {
        id: `m-user-${Date.now()}`,
        role: "user",
        content: userText,
        timestamp: now,
      },
      {
        id: `m-assistant-${Date.now() + 1}`,
        role: "assistant",
        content: batchAssistantText(batch, ctx.topic),
        timestamp: now,
        proposals: batchProposals(batch, ctx.topic),
      },
    ];
  }

  if (ctx.kind === "correlation") {
    return [
      {
        id: `m-user-${Date.now()}`,
        role: "user",
        content: `Tell me more about the ${ctx.ref.replace(/_/g, " ")} correlation.`,
        timestamp: now,
      },
      {
        id: `m-assistant-${Date.now() + 1}`,
        role: "assistant",
        content: `Looking at \`${ctx.ref}\` over your last 30 days of heats. Phase 0 — proposals + citations are canned. Phase 4 wires this turn to a real Claude call grounded in the correlation engine output + AFS handbook RAG.`,
        timestamp: now,
      },
    ];
  }

  if (ctx.kind === "entity") {
    return [
      {
        id: `m-user-${Date.now()}`,
        role: "user",
        content: `What should I know about entity \`${ctx.ref}\` right now?`,
        timestamp: now,
      },
      {
        id: `m-assistant-${Date.now() + 1}`,
        role: "assistant",
        content: `Reviewing live parameters, recent batches and active alarms for \`${ctx.ref}\`. Phase 4 grounds this in the tag stream + bindings + last 30 days of batches; Phase 0 returns this canned placeholder.`,
        timestamp: now,
      },
    ];
  }

  if (ctx.kind === "model_authoring") {
    return [
      {
        id: `m-assistant-${Date.now()}`,
        role: "assistant",
        content:
          "I can help you author the foundry process model. Tell me what equipment you'd like to add, what process you'd like to model, or paste a P&ID description. I'll propose entities + connections grounded in the foundry pack's template catalogue — each comes with one-click Accept to add it to the graph.\n\nA few starter prompts on the right →",
        timestamp: now,
      },
    ];
  }

  return [];
}

// --- Model-authoring: turn a free-text user prompt into proposals -----

/**
 * Phase 0 — pattern-match the user's input against a few known foundry
 * authoring intents and return canned proposals with real `apply`
 * payloads. Phase 4 replaces this with a Claude tool-call.
 *
 * Returns null if the prompt doesn't match a known pattern — the caller
 * falls back to a generic acknowledgement.
 */
export function generateAuthoringTurn(userText: string): CopilotMessage | null {
  const text = userText.toLowerCase();
  const now = new Date().toISOString();

  // "Add a second induction furnace" / "add another furnace" / "Furnace 3"
  if (/(add|another|second|third|new).*furnace/.test(text) || /furnace\s*[2-9]/.test(text)) {
    const num = text.match(/furnace\s*([2-9])/)?.[1] ?? "3";
    const name = `Furnace ${num}`;
    return {
      id: `m-${Date.now()}`,
      role: "assistant",
      content: `Adding ${name} as a second induction furnace alongside Furnace 1. I'll use the \`foundry.induction_furnace\` template — defaults to 1000 kg / 500 kW. I'll also propose a connection to the existing Charge Prep Bay and to the Auto-Pour Station so it slots into the existing line.`,
      timestamp: now,
      proposals: [
        {
          id: `p-${Date.now()}-1`,
          kind: "entity",
          title: `Add ${name} (foundry.induction_furnace)`,
          rationale: `Inherits from the induction-furnace template — 4 parameters, 1 input port (furnace_charge), 2 output ports (molten_metal + slag).`,
          citations: [{ type: "catalog", ref: "foundry.induction_furnace" }],
          actions: ["accept", "edit", "reject"],
          apply: { type: "add_entity", templateId: "foundry.induction_furnace", instanceName: name },
        },
        {
          id: `p-${Date.now()}-2`,
          kind: "connection",
          title: `Connect Charge Prep Bay → ${name}`,
          rationale: `Both furnaces should draw from the same charge prep — common pattern in twin-furnace foundries.`,
          actions: ["accept", "edit", "reject"],
          apply: {
            type: "add_connection",
            source: "n_charge",
            target: "n_new_furnace",
            sourceHandle: "out_charge",
            targetHandle: "in_charge",
            label: "Furnace charge",
            materialClass: "ferrous_charge",
          },
        },
        {
          id: `p-${Date.now()}-3`,
          kind: "clarifying_question",
          title: `Should ${name} feed the existing pour station, or a new one?`,
          actions: ["accept", "edit"],
        },
      ],
    };
  }

  // "Add a tilt furnace" — different template
  if (/tilt|cupola/.test(text) && /furnace/.test(text)) {
    return {
      id: `m-${Date.now()}`,
      role: "assistant",
      content: `Tilt furnaces typically run alongside induction furnaces as backup. I'll propose adding it with the same induction-furnace template (a tilt-specific subtemplate is a Phase-2 pack addition). It'll start unconnected — you can wire it into the line once you confirm the geometry.`,
      timestamp: now,
      proposals: [
        {
          id: `p-${Date.now()}-1`,
          kind: "entity",
          title: "Add Tilt Furnace (backup)",
          rationale: "Same template family as the main furnace. Connect to charge prep + pour station once placed.",
          citations: [{ type: "catalog", ref: "foundry.induction_furnace" }],
          actions: ["accept", "edit", "reject"],
          apply: { type: "add_entity", templateId: "foundry.induction_furnace", instanceName: "Tilt Furnace (backup)" },
        },
      ],
    };
  }

  // "Add a second moulding line" / "another DISA"
  if (/(add|another|second|new).*(moulding|disa)/.test(text)) {
    return {
      id: `m-${Date.now()}`,
      role: "assistant",
      content: `Adding a second DISA moulding line as a parallel branch. It'll need its own sand-supply route. Phase 0 — I'll add the entity; you can wire the connections manually or pick a connection proposal below.`,
      timestamp: now,
      proposals: [
        {
          id: `p-${Date.now()}-1`,
          kind: "entity",
          title: "Add DISA Line 2 (foundry.moulding_machine_disa)",
          rationale: "Same template as Moulding Line 1. 2 inputs, 1 output.",
          citations: [{ type: "catalog", ref: "foundry.moulding_machine_disa" }],
          actions: ["accept", "edit", "reject"],
          apply: { type: "add_entity", templateId: "foundry.moulding_machine_disa", instanceName: "DISA Line 2" },
        },
        {
          id: `p-${Date.now()}-2`,
          kind: "connection",
          title: "Connect Sand Mixer A → DISA Line 2",
          rationale: "Both moulding lines can share the same sand supply.",
          actions: ["accept", "edit", "reject"],
          apply: {
            type: "add_connection",
            source: "n_sand",
            target: "n_new_moulding",
            sourceHandle: "out_sand",
            targetHandle: "in_sand",
            label: "Mixed sand",
            materialClass: "green_sand",
          },
        },
      ],
    };
  }

  // "Add a spectrometer" / "add another spectrometer"
  if (/spectro|spectrometer/.test(text)) {
    return {
      id: `m-${Date.now()}`,
      role: "assistant",
      content: `Adding another OES spectrometer. Useful as a backup for the existing one, or for a second sample station. I'll add the entity; pick a connection proposal to wire it up.`,
      timestamp: now,
      proposals: [
        {
          id: `p-${Date.now()}-1`,
          kind: "entity",
          title: "Add OES Spectrometer 2",
          rationale: "Same template — INSTRUMENT, PASSTHROUGH lot op.",
          citations: [{ type: "catalog", ref: "foundry.oes_spectrometer" }],
          actions: ["accept", "edit", "reject"],
          apply: { type: "add_entity", templateId: "foundry.oes_spectrometer", instanceName: "OES Spectrometer 2" },
        },
      ],
    };
  }

  // "Add a shot blast" / "add finishing" — finishing equipment
  if (/shot.?blast|finish|polish|grind/.test(text)) {
    return {
      id: `m-${Date.now()}`,
      role: "assistant",
      content: `Adding a shot blast cabinet for finishing. The foundry pack doesn't have a dedicated shot-blast template yet (Phase-2 pack addition), so I'll use the closest match — the cooling line template — and you can rename. The platform will flag the mismatch.`,
      timestamp: now,
      proposals: [
        {
          id: `p-${Date.now()}-1`,
          kind: "entity",
          title: "Add Shot Blast Cabinet",
          rationale: "Standard finishing-line equipment. Uses cooling_line template as placeholder until a shot-blast template ships.",
          citations: [{ type: "catalog", ref: "foundry.cooling_line · placeholder" }],
          actions: ["accept", "edit", "reject"],
          apply: { type: "add_entity", templateId: "foundry.cooling_line", instanceName: "Shot Blast Cabinet" },
        },
      ],
    };
  }

  // "Model my plant from scratch" / "describe my P&ID" — multi-entity plant skeleton
  if (/(p.?id|model my plant|build.*plant|describe.*plant|from scratch|whole plant)/.test(text)) {
    const t = Date.now();
    return {
      id: `m-${t}`,
      role: "assistant",
      content: `I'll propose a standard grey-iron foundry skeleton — charge prep → induction furnace → spectrometer → moulding line → pour station → cooling → shakeout → QC. Accept what fits your plant; reject what doesn't; iterate from there. Each entity inherits its pack template, so a Versatile sand tester etc. can be added separately once the line is in.`,
      timestamp: now,
      proposals: [
        {
          id: `p-${t}-1`,
          kind: "entity",
          title: "Add Charge Prep Bay",
          actions: ["accept", "edit", "reject"],
          citations: [{ type: "catalog", ref: "foundry.charge_preparation_bay" }],
          apply: { type: "add_entity", templateId: "foundry.charge_preparation_bay", instanceName: "Charge Prep Bay (proposed)" },
        },
        {
          id: `p-${t}-2`,
          kind: "entity",
          title: "Add Induction Furnace",
          actions: ["accept", "edit", "reject"],
          citations: [{ type: "catalog", ref: "foundry.induction_furnace" }],
          apply: { type: "add_entity", templateId: "foundry.induction_furnace", instanceName: "Furnace (proposed)" },
        },
        {
          id: `p-${t}-3`,
          kind: "entity",
          title: "Add OES Spectrometer",
          actions: ["accept", "edit", "reject"],
          citations: [{ type: "catalog", ref: "foundry.oes_spectrometer" }],
          apply: { type: "add_entity", templateId: "foundry.oes_spectrometer", instanceName: "Spectrometer (proposed)" },
        },
        {
          id: `p-${t}-4`,
          kind: "entity",
          title: "Add Sand Mixer",
          actions: ["accept", "edit", "reject"],
          citations: [{ type: "catalog", ref: "foundry.sand_mixer" }],
          apply: { type: "add_entity", templateId: "foundry.sand_mixer", instanceName: "Sand Mixer (proposed)" },
        },
        {
          id: `p-${t}-5`,
          kind: "entity",
          title: "Add DISA Moulding Line",
          actions: ["accept", "edit", "reject"],
          citations: [{ type: "catalog", ref: "foundry.moulding_machine_disa" }],
          apply: { type: "add_entity", templateId: "foundry.moulding_machine_disa", instanceName: "DISA Line (proposed)" },
        },
        {
          id: `p-${t}-6`,
          kind: "clarifying_question",
          title: "Which pack is this for — grey iron, ductile, or both?",
          actions: ["accept", "edit"],
        },
      ],
    };
  }

  // "Duplicate the existing line" / "copy this line"
  if (/(duplicate|copy|clone).*(line|plant|model)/.test(text)) {
    const t = Date.now();
    return {
      id: `m-${t}`,
      role: "assistant",
      content: `I can duplicate the metal line as a parallel branch — useful for a second pour line or a backup train. Accepting will add new instances of the main metal-line entities (Charge Prep 2, Furnace 2, Pour 2, Cooling 2, Shakeout 2). Connections won't be wired automatically — the Phase-4 AI casting consultant proposes them per-segment.`,
      timestamp: now,
      proposals: [
        {
          id: `p-${t}-1`,
          kind: "entity",
          title: "Add Charge Prep Bay 2",
          actions: ["accept", "edit", "reject"],
          apply: { type: "add_entity", templateId: "foundry.charge_preparation_bay", instanceName: "Charge Prep Bay 2" },
        },
        {
          id: `p-${t}-2`,
          kind: "entity",
          title: "Add Furnace 2",
          actions: ["accept", "edit", "reject"],
          apply: { type: "add_entity", templateId: "foundry.induction_furnace", instanceName: "Furnace 2" },
        },
        {
          id: `p-${t}-3`,
          kind: "entity",
          title: "Add Pour Station 2",
          actions: ["accept", "edit", "reject"],
          apply: { type: "add_entity", templateId: "foundry.auto_pour_station", instanceName: "Pour Station 2" },
        },
        {
          id: `p-${t}-4`,
          kind: "entity",
          title: "Add Cooling Line 2",
          actions: ["accept", "edit", "reject"],
          apply: { type: "add_entity", templateId: "foundry.cooling_line", instanceName: "Cooling Line 2" },
        },
      ],
    };
  }

  // "Show me the full model" / "list current entities"
  if (/(show|list).*(model|plant|entities|equipment)/.test(text)) {
    return {
      id: `m-${Date.now()}`,
      role: "assistant",
      content: `Open the Process Graph view (or click any node) — the side drawer shows the live list of templated entities, their input/output ports, parameters and tag bindings. I can summarise here too:\n\n• Furnace 1 + 2 (induction)\n• OES Spectrometer\n• Sand Mixer A + Versatile testers (VCCD-I, VUD-III, VPD-II, VMHD, Moisture)\n• DISA Moulding Line + Auto-Pour Station\n• Cooling Line + Shakeout\n• Visual / NDT Inspection + Tensile Testing\n\nNeed a CSV export? That ships in Phase 4 alongside the model-management API.`,
      timestamp: now,
    };
  }

  // "Compensate my temperature" / "Add a computed tag" / "Smooth this signal" — computed tag
  if (
    /(compensate|smooth|filter|derive|computed|virtual)\s*(tag|temp|temperature|signal|reading)/.test(text) ||
    /noisy|drift(ing)?|cold.junction/.test(text)
  ) {
    const t = Date.now();
    return {
      id: `m-${t}`,
      role: "assistant",
      content: `If the raw bath temperature is showing cable / cold-junction drift, the standard fix is a linear compensation against an ambient sensor. I'd propose a new computed tag (\`compensated_bath_temp\`) that subtracts ~2.4% of the bath-to-ambient delta from the raw reading — quiet on cold days, transparent on warm ones. It'll appear as a first-class tag in your catalog: bind it to widgets, alarm on it, correlate with batch outcomes.`,
      timestamp: now,
      proposals: [
        {
          id: `p-${t}-1`,
          kind: "entity",
          title: "Add computed tag · Compensated bath temperature",
          rationale:
            "K-thermocouple cold-junction compensation. Output is in °C, classified analog_scalar — fully compatible with KPI / Gauge / SPC / Histogram widgets.",
          citations: [
            { type: "preset", ref: "k-thermocouple-compensated" },
            { type: "tenant_data", ref: "Furnace 1 · bath_temp + ambient · last 24 h" },
          ],
          actions: ["accept", "edit", "reject"],
          apply: {
            type: "add_computed_tag",
            def: {
              name: "Compensated bath temperature",
              description:
                "Linear cold-junction compensation on furnace bath thermocouple. Removes ~2.4% of bath-to-ambient delta.",
              outputUnit: "°C",
              outputDataKind: "analog_scalar",
              inputs: [
                { tagId: "tag-furn1-bath-temp", alias: "T_raw" },
                { tagId: "tag-furn1-ambient", alias: "T_amb" },
              ],
              expression: "T_raw - 0.024 * (T_raw - T_amb)",
              trigger: { kind: "on_input_change", debounceMs: 250 },
              presetId: "k-thermocouple-compensated",
            },
          },
        },
        {
          id: `p-${t}-2`,
          kind: "clarifying_question",
          title: "Want me to also add an EMA-smoothed copy?",
          actions: ["accept", "reject"],
        },
      ],
    };
  }

  // "Add a pharma" / "non-foundry equipment" — pack mismatch
  if (/pharma|chemical|food|beverage|brewery|bioreactor/.test(text)) {
    return {
      id: `m-${Date.now()}`,
      role: "assistant",
      content: `The foundry pack doesn't include pharma / food / chemical entity templates. Those land in their respective packs (\`packs/pharma/\`, \`packs/food/\` — Phase 5+ when paying customers in those verticals materialise). For now I can only propose entities from the foundry catalogue.\n\nIf you have a real industrial use case in mind that doesn't fit, the workaround is to use the closest foundry template as a placeholder and override the parameters.`,
      timestamp: now,
    };
  }

  return null;
}

export type { ProposalApply };

// --- Canned text generators --------------------------------------------
//
// WS-E: these Phase-0 starters used to explain defects by MECHANISM — steam
// pickup in the sand, the casting contracting freely as it froze, "a thermal
// issue" — each attributed to an AFS handbook clause as though the platform had
// solved the physics. Stage 1 measures and correlates; it does not simulate.
// The text now states what was OBSERVED and what it co-occurs with, and where a
// handbook is named it is named as further reading for the operator, not as
// this platform's causal finding.
//
// The barred phrasings are listed in `physicsClaimPhrases`
// (platform-core/internal/twin/connectivity_physics_test.go) and that gate
// scans this whole file, comments included — so this note describes the removed
// claims rather than quoting them. That is deliberate: a comment carrying the
// sentence verbatim is one copy-paste from being a string again, and sourcemaps
// ship comments to the browser anyway (the WS-C#2 finding).
//
// Reachability, measured rather than assumed: every branch here keys off the
// FX_ALARMS / FX_BATCHES fixture ids, and the batch branch returns [] when the
// ref matches no fixture — so a live org's real heat number (H-9000xx) reaches
// none of this prose. It is stub/demo-mode text. It is reframed anyway because
// "unreachable today" is a property of the current router, not of the strings.

function alarmAssistantText(alarm: (typeof FX_ALARMS)[number]): string {
  const title = alarm.title.toLowerCase();
  if (title.includes("bath temperature")) {
    return `Bath temperature on Furnace 1 is above the 1500 °C limit. Looking at \`furnace_1.bath_temp\` for the last 60 min — the reading has held above 1500 °C for 38 s, with peak 1512 °C. Cross-checking your heat tracking: this corresponds to the end-of-melt hold window on heat H-04821. Three plausible causes ranked by historical pattern matching:\n\n1. Operator over-shot the setpoint after a charge addition (most common — 64% of past trips)\n2. Power controller PID overshoot after coil reactivation (24%)\n3. Temperature probe drift (sensor reading higher than actual) — least likely (8%); rule out by comparing against the secondary thermocouple\n\nIf you accept, I'll set up an alarm rule that triggers earlier (>1485 °C for 60 s) so the operator has more time to react.`;
  }
  if (title.includes("sand moisture")) {
    return `Sand moisture from \`sand_mixer_1.moisture\` has trended above 4.2% on 6 of the last 18 heats. Over the last 30 days, heats with sand moisture > 4.2% show more porosity defects — Pearson r = +0.42 (n=480 heats, p<0.001). That is a correlation in your own data, not a mechanism this platform has modelled; AFS Casting Defects Handbook §4.2 discusses the moisture–porosity relationship if you want the metallurgy.\n\nWorth checking next: ambient humidity this shift, and the bentonite-water ratio in the mixer. Recommended actions below.`;
  }
  if (title.includes("pour temperature")) {
    return `Pour temperature dropped below the 1360 °C minimum on \`pour_station_1.pour_temperature\` for 14 s. From the linked heat H-04818, the corresponding misrun rate spiked to 27% (vs the 12% baseline). This matches the historical pattern in your data — pour_temperature < 1360 °C co-occurs with misruns at r = -0.19 over the last 500 heats.\n\nOne thing that moved alongside it: ladle holding time exceeded the planned window (heat was held 18 min, baseline 12 min). Recommended actions below.`;
  }
  if (title.includes("gateway offline")) {
    return `The Tilt Furnace (backup) gateway hasn't reported in 48 hours. Last seen reading was normal. Most common causes ranked: power loss to the gateway PC (52%), MQTT broker connectivity issue (28%), gateway service crashed and didn't restart (15%), cert renewal failure (5%).`;
  }
  if (title.includes("carbon")) {
    return `Carbon on heat H-04812 came back at 2.92% — outside the 3.0–3.3% target band. Over your last 500 heats, carbon < 3.0% drives a 2× rejection rate. The mechanicals retest is the right call; in parallel I'd check the charge composition entry for that heat (operator may have logged a lower pig-iron ratio).`;
  }
  return `Investigating alarm "${alarm.title}". Phase 0 — this is a canned response. Phase 4 wires this turn to a real Claude call grounded in your tag stream + the foundry pack's defect taxonomy + AFS clauses.`;
}

function alarmProposals(alarm: (typeof FX_ALARMS)[number]): CopilotMessage["proposals"] {
  const title = alarm.title.toLowerCase();
  if (title.includes("bath temperature")) {
    return [
      {
        id: "p-1",
        kind: "correlation_rule",
        title: "Tighten the bath-temp alarm to >1485 °C for 60 s",
        rationale:
          "Earlier warning gives the operator a 12-second action window before the hard limit is exceeded.",
        citations: [{ type: "tenant_data", ref: "Last 30 days · 64% of trips matched this pattern" }],
        actions: ["accept", "edit", "reject"],
      },
      {
        id: "p-2",
        kind: "clarifying_question",
        title: "Has the secondary thermocouple been calibrated in the last 90 days?",
        actions: ["accept", "edit"],
      },
    ];
  }
  if (title.includes("sand moisture")) {
    return [
      {
        id: "p-1",
        kind: "correlation_rule",
        title: "Add sand_moisture > 4.2% → porosity-risk alarm",
        rationale:
          "Sand moisture above 4.2% predicts porosity with r=+0.42 (n=480 heats, p<0.001).",
        citations: [
          { type: "afs", ref: "AFS Casting Defects Handbook §4.2", excerpt: "Excess moisture in green sand…" },
          { type: "tenant_data", ref: "Last 30 days · 480 heats" },
        ],
        actions: ["accept", "edit", "reject"],
      },
      {
        id: "p-2",
        kind: "clarifying_question",
        title: "Want me to look at the bentonite-water ratio on the last 3 mixer batches?",
        actions: ["accept", "edit"],
      },
    ];
  }
  if (title.includes("pour temperature")) {
    return [
      {
        id: "p-1",
        kind: "correlation_rule",
        title: "Add pour_temperature < 1360 °C → misrun-risk alarm",
        rationale:
          "Pour temperature below 1360 °C historically drives misruns up to 27% (vs 12% baseline).",
        citations: [{ type: "tenant_data", ref: "Last 500 heats · r=-0.19" }],
        actions: ["accept", "edit", "reject"],
      },
      {
        id: "p-2",
        kind: "entity",
        title: "Schedule a maintenance check on the ladle pre-heat",
        rationale:
          "Extended ladle holding times suggest the pre-heat may not be reaching target — adds heat loss between tap and pour.",
        actions: ["accept", "edit", "reject"],
      },
    ];
  }
  return undefined;
}

function batchAssistantText(
  batch: (typeof FX_BATCHES)[number],
  topic?: string,
): string {
  if (batch.status === "pass") {
    return `Heat ${batch.heatNumber} passed QC — ${batch.castingsAccepted} of ${batch.castingsTotal} castings accepted (${Math.round((batch.castingsRejected / batch.castingsTotal) * 100)}% reject). Key parameters: pour_temperature ${batch.pourTemp} °C, moisture ${batch.moisture}%, carbon ${batch.carbon}%. All within target bands. Nothing requires investigation.`;
  }
  const focus = topic ? ` Focus on ${topic}.` : "";
  if (batch.defectType === "porosity") {
    return `Heat ${batch.heatNumber} failed with ${batch.castingsRejected} of ${batch.castingsTotal} castings rejected (${Math.round((batch.castingsRejected / batch.castingsTotal) * 100)}%) — primary defect was porosity.${focus}\n\nLooking at the heat's parameters: sand_mixer.moisture was ${batch.moisture}% at moulding stage — that's above the 4.2% threshold that historically correlates with porosity (r=+0.42 over your last 500 heats). pour_temperature was ${batch.pourTemp} °C (within band) and carbon was ${batch.carbon}% (within band), so the chemistry parameters this heat recorded were unremarkable. AFS Casting Defects Handbook §4.2 covers the moisture–porosity relationship; the platform is reporting the correlation, not the mechanism.\n\nThe parameter to look at first is the sand mixer's water feed. The retro-fix is to add an alarm; the forward-fix is a tighter feedback control on the mixer.`;
  }
  if (batch.defectType === "misrun") {
    return `Heat ${batch.heatNumber} failed with ${batch.castingsRejected} misruns of ${batch.castingsTotal}. pour_temperature was ${batch.pourTemp} °C — below the 1360 °C threshold below which misruns spike sharply in your history. Carbon was ${batch.carbon}% and moisture was ${batch.moisture}% — both within target band, so the parameter that stands out on this heat is the pour temperature.\n\nThe parameter that moved with it: ladle holding time between tap and pour. Recommended actions below.`;
  }
  if (batch.defectType === "shrinkage") {
    return `Heat ${batch.heatNumber} failed with ${batch.castingsRejected} shrinkage defects. Checking the moulding parameters: squeeze_pressure was likely outside the 7–9 bar window (the typical predictor for shrinkage in your data). Pour temperature ${batch.pourTemp} °C was within band; chemistry within band.\n\nThe parameter to check first is squeeze pressure — in your data, shrinkage rejects cluster on heats that moulded outside the 7–9 bar window. AFS Handbook §2.4 covers the compaction–shrinkage relationship; the typical fix is to nudge squeeze pressure back into that window.`;
  }
  if (batch.defectType === "blow_hole") {
    return `Heat ${batch.heatNumber} failed with ${batch.castingsRejected} blow-hole defects. Moisture was ${batch.moisture}% at moulding (above the 4.2% threshold). Moisture and sand permeability are the two parameters that most often move with blow holes in this data set — AFS Handbook §3.2 covers why. What your sand mixer recorded is consistent with that pattern; the platform is matching a pattern, not diagnosing the cavity.`;
  }
  return `Heat ${batch.heatNumber} failed — ${batch.castingsRejected} castings rejected. Primary defect: ${batch.defectType?.replace(/_/g, " ")}. Phase 0 — this analysis is canned. Phase 4 wires this turn to a real Claude call grounded in the heat's full parameter trace + your foundry pack corpus.`;
}

function batchProposals(
  batch: (typeof FX_BATCHES)[number],
  _topic?: string,
): CopilotMessage["proposals"] {
  if (batch.status === "pass") return undefined;
  if (batch.defectType === "porosity") {
    return [
      {
        id: "p-1",
        kind: "correlation_rule",
        title: "Add a sand-moisture > 4.2% → porosity alarm",
        rationale: "Heat moisture was 4.45% — above the threshold that drives porosity in your last 30 days of data.",
        citations: [
          { type: "afs", ref: "AFS Casting Defects Handbook §4.2" },
          { type: "tenant_data", ref: "Last 500 heats · r=+0.42" },
        ],
        actions: ["accept", "edit", "reject"],
      },
      {
        id: "p-2",
        kind: "clarifying_question",
        title: "Should I review the last 3 days of mixer water-feed data?",
        actions: ["accept", "edit"],
      },
    ];
  }
  if (batch.defectType === "misrun") {
    return [
      {
        id: "p-1",
        kind: "correlation_rule",
        title: "Add pour_temperature < 1360 °C alarm",
        rationale: "Heat ran at 1352 °C — below the threshold. Misruns spike historically below 1360.",
        citations: [{ type: "tenant_data", ref: "Last 500 heats · r=-0.19" }],
        actions: ["accept", "edit", "reject"],
      },
    ];
  }
  if (batch.defectType === "shrinkage") {
    return [
      {
        id: "p-1",
        kind: "correlation_rule",
        title: "Add squeeze_pressure outside 7–9 bar → shrinkage-risk alarm",
        rationale: "U-shaped relationship between squeeze pressure and shrinkage in your data.",
        citations: [{ type: "afs", ref: "AFS Casting Defects Handbook §2.4" }],
        actions: ["accept", "edit", "reject"],
      },
    ];
  }
  return undefined;
}
